import AdyenCheckout from '@adyen/adyen-web';
import DropinElement from '@adyen/adyen-web/dist/types/components/Dropin';
import AdyenCheckoutError from '@adyen/adyen-web/dist/types/core/Errors/AdyenCheckoutError';
import { CoreOptions as AdyenOptions } from '@adyen/adyen-web/dist/types/core/types';
import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import IPaymentProvider from './IPaymentProvider';
import PaymentProviderSubmitResult from './PaymentProviderSubmitResult';

class PaymentProvider implements IPaymentProvider {
  private component: DropinElement | null = null;

  private subscribers: any[];

  private updatedAmount: number | undefined;

  constructor(private configuration: AdyenOptions) {
    this.subscribers = [];
  }

  getType(): 'PaymentProvider' {
    return 'PaymentProvider';
  }

  get isValid(): boolean {
    return this.component?.isValid ?? false;
  }

  stringify(value: any) {
    if (value == null) {
      return null;
    }
    return JSON.stringify(value);
  }

  notifySubscribers(paymentMethodData: any, additionalData: any, error: AdyenCheckoutError | null, isCancelled = false) {
    let subscriber;
    while (subscriber = this.subscribers.pop()) {
      subscriber({
        paymentMethodData: this.stringify(paymentMethodData),
        additionalData: this.stringify(additionalData),
        error,
        isCancelled,
      });
    }
  }

  async setup($target: string | HTMLElement) {
    const finalConfiguration = {
      ...this.configuration,
      ...{
        onSubmit: (state: any) => {
          this.notifySubscribers(state.data, null, null);
        },

        onAdditionalDetails: (state: any) => {
          this.notifySubscribers(null, state.data, null);
        },

        onError: (error: AdyenCheckoutError) => {
          if (error.name === 'CANCEL') {
            this.notifySubscribers(null, null, null, true);
          } else {
            this.notifySubscribers(null, null, error);
          }
        },
      },
    };

    const checkout = await AdyenCheckout(finalConfiguration);
    this.component = checkout.create('dropin').mount($target);
  }

  async reload($target: null | HTMLElement, clientUpdatedAmount: number) {
    this.updatedAmount = Math.floor(clientUpdatedAmount * 100);

    const { paymentMethodsConfiguration } = this.configuration;

    if (paymentMethodsConfiguration) {
      const { applepay } = paymentMethodsConfiguration;

      if (applepay) {
        applepay.amount.value = this.updatedAmount;
      }
    }

    const checkout = await AdyenCheckout(this.configuration);

    const dropInElement = checkout.create('dropin');

    this.component?.update(dropInElement.props);
  }

  submit(): Promise<PaymentProviderSubmitResult> {
    if (this.component?.isValid) {
      const promise = this.subscribe();

      this.component?.submit();

      return promise;
    }

    return Promise.resolve({
      isCancelled: false,
      additionalData: null,
      error: null,
      paymentMethodData: null,
    });
  }

  enable() {
    this.component?.setStatus('ready');
  }

  disable() {
    this.component?.setStatus('loading');
  }

  subscribe() {
    const promise = new Promise<PaymentProviderSubmitResult>((resolve) => {
      this.subscribers.push(resolve);
    });

    return promise;
  }

  additionalAction(action: PaymentAction) {
    this.component?.handleAction(action);
  }

  showValidation() {
    this.component?.showValidation();
  }
}

export default PaymentProvider;
